(() => {
  const ACCORDION_REGION_NAMES = {
    UK: 'UK',
    US: 'US',
    NOR: 'NORWAY',
    EUR: 'EUROPE',
  };

  function parseRegions(value) {
    return String(value || '')
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  function getSelectedOptions(variantSelects) {
    const options = [];

    variantSelects.querySelectorAll('fieldset[data-option-name], .product-form__input--dropdown').forEach((group) => {
      const name = group.dataset.optionName;
      if (!name) return;

      const checked = group.querySelector('input[type="radio"]:checked:not([data-variant-region-input])');
      const select = group.querySelector('select');
      const value = checked?.value || select?.value;

      if (value) options.push({ name, value });
    });

    return options;
  }

  function findVariant(config, selectedOptions, region) {
    return config.variants.find((variant) => {
      const regions = (variant.regions || []).filter(Boolean);
      if (regions.length && !regions.includes(region)) return false;

      return selectedOptions.every((option) => {
        const index = config.optionNames.indexOf(option.name);
        if (index === -1) return true;
        return variant.options[index] === option.value;
      });
    });
  }

  function findFirstAvailableSizeInput(variantSelects, region) {
    const sizeFieldset = variantSelects.querySelector('fieldset[data-option-name="Size"]');
    if (!sizeFieldset) return null;

    return Array.from(sizeFieldset.querySelectorAll('input[type="radio"]:not(.disabled)')).find((input) => {
      if (input.classList.contains('variant-region-option--hidden')) return false;
      return parseRegions(input.dataset.variantRegions).includes(region);
    });
  }

  function getActiveRegion(root, config) {
    const productInfo = root.closest('product-info');
    return (
      productInfo?.dataset.userSelectedProductRegion ||
      root.dataset.selectedRegion ||
      config.defaultRegion
    );
  }

  function storeUserRegion(root, region) {
    const productInfo = root.closest('product-info');
    if (productInfo) productInfo.dataset.userSelectedProductRegion = region;
    root.dataset.selectedRegion = region;
  }

  function readPickerConfig(variantSelects) {
    const configScript = variantSelects?.querySelector('[data-variant-region-config]');
    if (!configScript) return null;

    try {
      return JSON.parse(configScript.textContent);
    } catch {
      return null;
    }
  }

  class VariantRegionPicker {
    constructor(root, config) {
      this.root = root;
      this.variantSelects = root.closest('variant-selects');
      this.config = config;
      this.productInfo = this.variantSelects.closest('product-info');
      this.selectedRegion = getActiveRegion(this.root, this.config);
      this.accordionLabel = this.root.querySelector('[data-variant-region-accordion-label]');
      this.regionInputs = this.root.querySelectorAll('[data-variant-region-input]');
      this.productForm = document.getElementById(this.config.productFormId);
      this.sizingLabel = this.config.sizingLabel || 'DUVET SIZING';
      this.requestId = 0;

      this.bindEvents();
      this.applyRegionUI(this.selectedRegion);
    }

    bindEvents() {
      this.regionInputs.forEach((input) => {
        input.addEventListener('change', (event) => {
          event.stopPropagation();
          if (!input.checked) return;

          this.selectedRegion = input.value;
          storeUserRegion(this.root, this.selectedRegion);
          this.applyRegionUI(this.selectedRegion);
          setPickerOpen(this.root, false);
          this.syncVariant();
        });
      });

      this.variantSelects.addEventListener('change', (event) => {
        if (event.target.matches('[data-variant-region-input]')) return;
        this.syncVariant();
      });
    }

    applyRegionUI(region) {
      if (this.accordionLabel) {
        this.accordionLabel.textContent = `${ACCORDION_REGION_NAMES[region] || region} ${this.sizingLabel}`;
      }

      this.regionInputs.forEach((input) => {
        input.checked = input.value === region;
      });

      this.variantSelects.querySelectorAll('[data-variant-regions]').forEach((input) => {
        const allowed = parseRegions(input.dataset.variantRegions).includes(region);
        input.classList.toggle('variant-region-option--hidden', !allowed);

        if (input.type === 'radio') {
          const label = this.variantSelects.querySelector(`label[for="${input.id}"]`);
          label?.classList.toggle('variant-region-option--hidden', !allowed);
        }
      });

      const sizeFieldset = this.variantSelects.querySelector('fieldset[data-option-name="Size"]');
      if (!sizeFieldset) return;

      sizeFieldset.querySelectorAll('input[type="radio"].variant-region-option--hidden:checked').forEach((input) => {
        input.checked = false;
      });

      const checkedSize = sizeFieldset.querySelector(
        'input[type="radio"]:checked:not(.variant-region-option--hidden)'
      );
      if (!checkedSize) {
        const fallbackSize = findFirstAvailableSizeInput(this.variantSelects, region);
        if (fallbackSize) fallbackSize.checked = true;
      }
    }

    syncVariant() {
      const selectedOptions = getSelectedOptions(this.variantSelects);
      let variant = findVariant(this.config, selectedOptions, this.selectedRegion);

      if (!variant) {
        const fallbackSize = findFirstAvailableSizeInput(this.variantSelects, this.selectedRegion);
        if (fallbackSize) {
          fallbackSize.checked = true;
          selectedOptions.forEach((option) => {
            if (option.name === 'Size') option.value = fallbackSize.value;
          });
          variant = findVariant(this.config, selectedOptions, this.selectedRegion);
        }
      }

      if (!variant) return;
      this.updateProductDetails(variant.id);
    }

    notifyDimensionsChange() {
      document.dispatchEvent(
        new CustomEvent('variant-region:change', {
          detail: { region: this.selectedRegion },
        })
      );
    }

    updateProductDetails(variantId) {
      if (!this.productInfo?.dataset.url) return;

      const variantInput = this.productForm?.querySelector('input[name="id"]');
      if (variantInput && String(variantInput.value) === String(variantId)) {
        this.notifyDimensionsChange();
        return;
      }

      const requestId = ++this.requestId;
      const requestUrl = `${this.productInfo.dataset.url}?variant=${variantId}&section_id=${this.productInfo.dataset.section}`;

      fetch(requestUrl)
        .then((response) => response.text())
        .then((responseText) => {
          if (requestId !== this.requestId) return;

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sectionId = this.productInfo.dataset.section;

          this.productInfo.updateVariantInputs(variantId);

          const updateBlock = (id, shouldHide = () => false) => {
            const source = html.getElementById(`${id}-${sectionId}`);
            const destination = document.getElementById(`${id}-${sectionId}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateBlock('price');
          updateBlock('Sku', ({ classList }) => classList.contains('hidden'));
          updateBlock('Inventory', ({ innerText }) => innerText === '');

          const fetchedVariantNode = html.querySelector('variant-selects [data-selected-variant]');
          const selectedVariantScript = this.variantSelects.querySelector('[data-selected-variant]');
          let variant = null;

          if (fetchedVariantNode) {
            try {
              variant = JSON.parse(fetchedVariantNode.textContent);
            } catch {
              variant = null;
            }

            if (selectedVariantScript) {
              selectedVariantScript.textContent = fetchedVariantNode.textContent;
            }
          }

          if (variant?.featured_media?.id) {
            this.productInfo.updateMedia(html, variant.featured_media.id);
          }

          if (variant?.price != null && this.variantSelects?.dataset) {
            this.variantSelects.dataset.selectedPriceAmount = String(variant.price / 100);
          }

          const fetchedSubmitButton = html.getElementById(`ProductSubmitButton-${sectionId}`);
          this.productInfo.productForm?.toggleSubmitButton(
            fetchedSubmitButton?.hasAttribute('disabled') ?? true,
            window.variantStrings?.soldOut
          );

          if (typeof this.productInfo.updateURL === 'function') {
            this.productInfo.updateURL(this.productInfo.dataset.url, variantId);
          }

          this.notifyDimensionsChange();
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }

  function setPickerOpen(root, open) {
    if (!root) return;
    const toggle = root.querySelector('.variant-region-picker__toggle');
    root.classList.toggle('variant-region-picker--open', open);
    toggle?.setAttribute('aria-expanded', String(open));
  }

  function togglePicker(root) {
    setPickerOpen(root, !root.classList.contains('variant-region-picker--open'));
  }

  function initPicker(root) {
    if (!root || root.dataset.variantRegionPickerReady) return;

    const variantSelects = root.closest('variant-selects');
    const config = readPickerConfig(variantSelects);
    if (!variantSelects || !config) return;

    root.dataset.variantRegionPickerReady = 'true';
    new VariantRegionPicker(root, config);
  }

  function initAllPickers() {
    document.querySelectorAll('[data-variant-region-picker]').forEach((root) => initPicker(root));
  }

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('.variant-region-picker__toggle');
    if (!toggle) return;

    event.preventDefault();
    event.stopPropagation();

    const root = toggle.closest('[data-variant-region-picker]');
    if (root) togglePicker(root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllPickers);
  } else {
    initAllPickers();
  }

  document.addEventListener('shopify:section:load', initAllPickers);
})();
